import { useEffect, useState } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { Avatar, Box, Container, Skeleton } from '@mui/material';
import CustomText from 'components/common/CustomText/CustomText';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import FilterIcon from 'icons/FilterIcon';
import useGlobalStyles from 'styles/globalStyles';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import CustomCellLabel from 'components/tables/AllUsers/CustomCellLabel';
import CustomCellValue from 'components/tables/AllUsers/CustomCellValue';
import { tableColumnProps } from 'common/constants';
import { toTitleCase } from 'common/utils';
import { snackbarService } from 'services/snackbarService';
import { ContentReportsFiltersDto } from 'types/dto/ContentReportsFiltersDto';
import { ListContentReportsDto } from 'types/dto/ListContentReportsDto';
import { EContentReportStatus, EContentReportTarget } from 'enum/EContentReport';
import { useGetContentReports, useResolveContentReport } from 'hooks/useContentReports';
import ContentReportsFilters from './ContentReportsFilters';
import ContentReportActionButtons from 'components/tables/ActionButtons/ContentReportActionButtons';
import ContentReportModal from 'components/modals/ContentReportModal/ContentReportModal';

// Mistakes shoppers reported in what the app wrote, open ones first by default,
// each with the product or ingredient it is about and the part that was wrong.
export default function ContentReportsTable() {
  const globalStyles = useGlobalStyles();
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  // The report open in the details modal.
  const [viewed, setViewed] = useState<any | null>(null);

  const [filters, setFilters] = useState<ContentReportsFiltersDto>({
    status: EContentReportStatus.OPEN,
    targetType: 'all',
    section: 'all',
  });

  const requestParams: ListContentReportsDto = {
    page: page + 1,
    size,
    ...filters,
  };

  const { data, refetch, isLoading } = useGetContentReports(requestParams);

  const { mutate: resolve } = useResolveContentReport(
    () => {
      setResolvingId(null);
      setViewed(null);
      refetch();
    },
    (error) => {
      setResolvingId(null);
      snackbarService.showSnackbar(error?.response?.data?.message ?? 'Could not resolve the report', 'error');
    },
  );

  const columns: GridColDef[] = [
    {
      field: 'targetName',
      ...tableColumnProps,
      renderHeader: () => (
        <CustomCellLabel text="Item" />
      ),
      renderCell: (params) => {
        return (
          <Box display={'flex'} alignItems={'center'} gap={1}>
            {params.row.targetType === EContentReportTarget.PRODUCT && (
              <Box>
                <Avatar
                  src={params.row.targetAvatar ?? undefined}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '5px',
                    bgcolor: colors.avatarBg,
                  }}
                />
              </Box>
            )}
            <CustomText
              text={params.row.targetName ?? params.row.targetId}
              fontSize={15}
              fontWeight={500}
              color={colors.black}
            />
          </Box>
        )
      }
    },
    {
      field: 'targetType',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Type" />
      ),
      renderCell: (params) => (
        <Box>
          <CustomCellValue text={toTitleCase(params.row.targetType)} />
        </Box>
      ),
    },
    {
      field: 'section',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Section" />
      ),
      renderCell: (params) => (
        <Box>
          <CustomCellValue text={toTitleCase(params.row.section)} />
        </Box>
      ),
    },
    {
      field: 'user',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Reported By" />
      ),
      renderCell: (params) => (
        <Box>
          <CustomCellValue text={params.row.user?.fullName ?? '-'} />
        </Box>
      ),
    },
    {
      field: 'createdAt',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Date" />
      ),
      renderCell: (params) => (
        <Box>
          <CustomCellValue text={new Date(params.row.createdAt).toLocaleDateString()} />
        </Box>
      ),
    },
    {
      field: 'status',
      ...tableColumnProps,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Status" />
      ),
      renderCell: (params) => (
        <Box>
          <CustomCellValue text={params.row.status === EContentReportStatus.RESOLVED ? 'Resolved' : 'Open'} />
        </Box>
      ),
    },
    {
      field: 'actions',
      ...tableColumnProps,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => (
        <CustomCellLabel text="Actions" />
      ),
      renderCell: (params) => {
        return (
          <Box onClick={(e) => e.stopPropagation()}>
            <ContentReportActionButtons onView={() => setViewed(params.row)} />
          </Box>
        )
      }
    },
  ];

  useEffect(() => {
    if (data?.totalCount !== undefined) {
      setRowCount(data.totalCount);
    }
  }, [data?.totalCount]);

  return (
    <Container disableGutters maxWidth={'xl'}>
      <Paper className={globalStyles.tableContainer} sx={{
        overflowX: 'auto',
        minHeight: 11 * 52 + 56, // 10 rows + header
      }}
      >

        {/* Header */}
        <Box
          display={{ sm: 'block', md: 'flex' }}
          justifyContent={'space-between'}
          gap={2}
          sx={{
            px: 2,
            py: 2.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
            <Box
              sx={{
                backgroundColor: colors.topBackground,
                display: 'inline-flex',
                height: 40,
                width: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '8px',
              }}
            >
              <FlagOutlinedIcon sx={{ color: colors.primary }} />
            </Box>
            <CustomText text="Content Reports" fontSize={16} color={colors.primary} fontWeight={600} />
          </Box>
          <Box display={'flex'} gap={2}>
            <CustomButton
              text='Filter'
              variant='outlined'
              height={40}
              icon={<FilterIcon />}
              onClick={() => setFiltersOpen(prev => !prev)}
            />
          </Box>
        </Box>

        <ContentReportsFilters
          open={filtersOpen}
          initialValues={filters}
          loading={isLoading}
          onApply={(values) => {
            setFilters(values);
            setPage(0);
          }}
          onReset={() => {
            setFilters({ status: EContentReportStatus.OPEN, targetType: 'all', section: 'all' });
            setPage(0);
          }}
        />

        {/* Data */}
        <DataGrid
          sx={{ minHeight: 11 * 52 + 56 }}
          disableColumnMenu
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          disableColumnSorting
          disableRowSelectionOnClick
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          slotProps={{
            loadingOverlay: {
              children: Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} height={52} />
              )),
            },
          }}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{
            page: page,
            pageSize: size,
          }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setSize(model.pageSize);
          }}
          pageSizeOptions={[10, 20, 30, 50, 100]}
          className={globalStyles.dataGrid}
        />
      </Paper>

      <ContentReportModal
        report={viewed}
        resolving={!!viewed && resolvingId === viewed.id}
        onResolve={(reportId) => {
          setResolvingId(reportId);
          resolve(reportId);
        }}
        onClose={() => setViewed(null)}
      />

      <Box mt={3} />
    </Container>
  );
}
